import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./HeroSection.css";

const heroSlides = [
  {
    eventId: 48,
    image: "/images/timeline/test.jpg",
    quote: (
      <>
        <span className="hero-clause">
          In 1843, Anna Atkins self-published{" "}
          <em className="hero-no-break">Photographs of British Algae</em>,
        </span>
        <span className="hero-clause">
          the first book photographically printed and illustrated.
        </span>
      </>
    ),
    quote_zh: (
      <>
        <span className="hero-clause">
          1843年，<span className="hero-no-break">Anna Atkins</span>
          自费出版《
          <span className="hero-no-break">Photographs of British Algae</span>
          》，
        </span>
        <span className="hero-clause">
          这是第一本以摄影图像印制并配图的书。
        </span>
      </>
    ),
  },
  {
    eventId: 13,
    image: "/images/timeline/test4.jpg",
    quote: (
      <>
        <span className="hero-clause">
          In 1890, Jacob Riis photographed women lodging{" "}
          <span className="hero-no-break">
            at the Elizabeth Street police station,
          </span>
        </span>
        <span className="hero-clause">
          turning photography into evidence of{" "}
          <span className="hero-no-break">urban poverty</span>.
        </span>
      </>
    ),
    quote_zh: (
      <>
        <span className="hero-clause">
          1890年，<span className="hero-no-break">Jacob Riis</span>拍下
          <span className="hero-no-break">Elizabeth Street</span>
          警察局里的女性借宿者，
        </span>
        <span className="hero-clause">
          让摄影成为揭示城市贫困的社会证词。
        </span>
      </>
    ),
  },
  {
    eventId: 20,
    image: "/images/timeline/test5.jpg",
    imageMobile: "/images/timeline/test6.jpg", // 手机端使用不同图片
    quote: (
      <>
        <span className="hero-clause">
          In 1917, Alfred Stieglitz photographed{" "}
          <span className="hero-no-break">Georgia O’Keeffe’s exhibition</span>{" "}
          <span className="hero-no-break">at 291</span>,
        </span>
        <span className="hero-clause">
          recording a defining moment in{" "}
          <span className="hero-no-break">American modernism</span>.
        </span>
      </>
    ),
    quote_zh: (
      <>
        <span className="hero-clause">
          1917年，<span className="hero-no-break">Alfred Stieglitz</span>在
          291画廊拍下<span className="hero-no-break">Georgia O’Keeffe</span>
          的个展，
        </span>
        <span className="hero-clause">
          见证她进入纽约现代艺术视野的重要时刻。
        </span>
      </>
    ),
  },
];

function HeroSection({ language = "EN" }) {
  const [slides, setSlides] = useState([
    { index: 0, isActive: true, isNext: false },
  ]);
  const [showText, setShowText] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [transitionTop, setTransitionTop] = useState(132);
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const textTimeoutRef = useRef(null);
  const wheelAccumRef = useRef(0);
  const wheelResetRef = useRef(null);
  const wheelLockRef = useRef(false);
  const heroSectionRef = useRef(null);
  const SLIDE_INTERVAL = 7000;
  const currentIndex = slides.find((slide) => slide.isActive)?.index || 0;

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 获取适合当前设备的图片路径
  const getImageForDevice = (slideData) => {
    return isMobile && slideData.imageMobile
      ? slideData.imageMobile
      : slideData.image;
  };

  // Swipe handling refs (mobile only)
  const touchStartYRef = useRef(0);
  const touchStartXRef = useRef(0);
  const touchActiveRef = useRef(false);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setShowText(false); // 先隐藏文字

      setTimeout(() => {
        const nextIndex = (currentIndex + 1) % heroSlides.length;

        // 添加下一张幻灯片
        setSlides([
          { index: currentIndex, isActive: true, isNext: false },
          { index: nextIndex, isActive: false, isNext: true },
        ]);

        // 开始动画
        setTimeout(() => {
          setSlides([
            { index: currentIndex, isActive: false, isNext: false },
            { index: nextIndex, isActive: true, isNext: false },
          ]);

          // 清理旧幻灯片
          setTimeout(() => {
            setSlides([{ index: nextIndex, isActive: true, isNext: false }]);
          }, 1000);
        }, 50);
      }, 300); // 文字淡出时间
    }, SLIDE_INTERVAL);

    return () => {
      clearTimeout(timeoutRef.current);
      clearTimeout(textTimeoutRef.current);
    };
  }, [currentIndex]);

  useEffect(() => {
    textTimeoutRef.current = setTimeout(() => {
      setShowText(true);
    }, 800); // 切换图片后再显示文字
    return () => clearTimeout(textTimeoutRef.current);
  }, [currentIndex]);

  // 统一的跳转触发（用于箭头点击或上滑手势）
  const triggerNavigateWithAnimation = (destination = "/timeline") => {
    if (isAnimating) return;

    // 桌面端导航由 Logo 栏、分割线和菜单栏共同组成，实际高度会随
    // 字体渲染与视口宽度产生细微变化。动画触发时读取真实底边，
    // 避免幕布越过菜单栏；移动端继续使用原有的固定终点。
    if (window.innerWidth > 768) {
      const navbar = document.querySelector(".navbar");
      const separators = navbar?.querySelectorAll(".line-separator");
      const menuBottomSeparator = separators?.[separators.length - 1];
      const menuBottom =
        menuBottomSeparator?.getBoundingClientRect().bottom ??
        navbar?.getBoundingClientRect().bottom;
      if (Number.isFinite(menuBottom)) {
        setTransitionTop(menuBottom);
      }
    } else {
      setTransitionTop(64);
    }

    setIsAnimating(true);
    setTimeout(() => {
      navigate(destination);
    }, 1050);
  };

  // 箭头点击，触发动画然后跳转
  const handleArrowClick = (e) => {
    e.preventDefault();
    triggerNavigateWithAnimation();
  };

  const handleEventClick = (e, eventId) => {
    // Preserve standard link behavior for new-tab and modified clicks.
    if (
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    ) {
      return;
    }

    e.preventDefault();
    triggerNavigateWithAnimation(`/timeline?event=${eventId}`);
  };

  // 仅在手机端启用：上滑即可进入 Timeline
  const handleTouchStart = (e) => {
    if (window.innerWidth > 768) return; // 仅手机端
    const t = e.touches && e.touches[0];
    if (!t) return;
    touchActiveRef.current = true;
    touchStartYRef.current = t.clientY;
    touchStartXRef.current = t.clientX;
  };

  const handleTouchEnd = (e) => {
    if (window.innerWidth > 768) return; // 仅手机端
    if (!touchActiveRef.current) return;
    touchActiveRef.current = false;
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    const deltaY = touchStartYRef.current - t.clientY; // 向上为正
    const deltaX = Math.abs(t.clientX - touchStartXRef.current);
    const thresholdY = 60; // 触发阈值，像素
    if (deltaY > thresholdY && deltaY > deltaX) {
      triggerNavigateWithAnimation();
    }
  };

  // 电脑端/触控板：下滑进入 Timeline（累计阈值避免误触）
  const handleWheel = (e) => {
    if (isAnimating || wheelLockRef.current) return;

    const deltaY = e.deltaY || 0;
    const deltaX = e.deltaX || 0;
    if (Math.abs(deltaY) <= Math.abs(deltaX)) return; // 忽略以横向为主的手势
    if (deltaY <= 0) return; // 仅下滑触发

    wheelAccumRef.current += deltaY;
    if (wheelResetRef.current) clearTimeout(wheelResetRef.current);
    wheelResetRef.current = setTimeout(() => {
      wheelAccumRef.current = 0;
    }, 180);

    const threshold = 120; // 触控板和滚轮都较稳妥的阈值
    if (wheelAccumRef.current >= threshold) {
      wheelAccumRef.current = 0;
      wheelLockRef.current = true;
      triggerNavigateWithAnimation();
      setTimeout(() => {
        wheelLockRef.current = false;
      }, 1200);
    }
  };

  useEffect(() => {
    return () => {
      if (wheelResetRef.current) clearTimeout(wheelResetRef.current);
    };
  }, []);

  // The Hero uses an upward swipe / downward wheel gesture to enter Timeline.
  // Prevent only that direction from reaching the browser's native bottom
  // overscroll, while leaving the opposite direction available for the
  // natural pull-down bounce introduced by the sticky header.
  useEffect(() => {
    const heroSection = heroSectionRef.current;
    if (!heroSection) return undefined;

    const preventUpwardTouchOverscroll = (event) => {
      if (!touchActiveRef.current) return;
      const touch = event.touches?.[0];
      if (!touch) return;

      const deltaY = touchStartYRef.current - touch.clientY;
      const deltaX = Math.abs(touch.clientX - touchStartXRef.current);
      if (deltaY > 0 && deltaY > deltaX && event.cancelable) {
        event.preventDefault();
      }
    };

    const preventDownwardWheelOverscroll = (event) => {
      const deltaY = event.deltaY || 0;
      const deltaX = event.deltaX || 0;
      if (
        deltaY > 0 &&
        Math.abs(deltaY) > Math.abs(deltaX) &&
        event.cancelable
      ) {
        event.preventDefault();
      }
    };

    heroSection.addEventListener(
      "touchmove",
      preventUpwardTouchOverscroll,
      { passive: false }
    );
    heroSection.addEventListener(
      "wheel",
      preventDownwardWheelOverscroll,
      { passive: false }
    );

    return () => {
      heroSection.removeEventListener(
        "touchmove",
        preventUpwardTouchOverscroll
      );
      heroSection.removeEventListener(
        "wheel",
        preventDownwardWheelOverscroll
      );
    };
  }, []);

  return (
    <section
      ref={heroSectionRef}
      className="hero-section-carousel"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
    >
      {slides.map((slide, idx) => (
        <div
          key={slide.index}
          className={`hero-slide ${slide.isActive ? "active" : ""} ${
            slide.isNext ? "next" : ""
          }`}
          style={{
            backgroundImage: `url(${getImageForDevice(
              heroSlides[slide.index]
            )})`,
          }}
        >
          {showText && slide.isActive && (
            <div className="hero-overlay-text" key={slide.index}>
              <a
                className="hero-event-link"
                href={`/timeline?event=${heroSlides[slide.index].eventId}`}
                aria-label={
                  language === "CN"
                    ? "在时间线中查看此事件"
                    : "View this event in the timeline"
                }
                onClick={(event) =>
                  handleEventClick(
                    event,
                    heroSlides[slide.index].eventId
                  )
                }
              >
                <blockquote>
                  {language === "CN"
                    ? heroSlides[slide.index].quote_zh ||
                      heroSlides[slide.index].quote
                    : heroSlides[slide.index].quote}
                </blockquote>
              </a>
            </div>
          )}
        </div>
      ))}

      {/* 底部跳动箭头 */}
      {!isAnimating && (
        <a
          href="/timeline"
          className="hero-down-arrow"
          aria-label={language === "CN" ? "进入时间线" : "Go to Timeline"}
          onClick={handleArrowClick}
        >
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 18L24 30L36 18"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      )}

      {/* 版权信息 */}
      <div className="hero-copyright"></div>

      {/* 白色遮罩动画 */}
      {isAnimating && (
        <div
          className="hero-transition-overlay"
          style={{
            "--hero-overlay-top": `${transitionTop}px`,
          }}
        ></div>
      )}
    </section>
  );
}

export default HeroSection;
